import React, { Component } from 'react';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';
import Raven from 'raven-js';
import { LOGIN_STATE } from '@zocdoc/components/src/Layout/constants';
import { PROCEDURE_IDS } from '@zocdoc/components/src/constants';
import { unauthorizedResponse } from '@zocdoc/components/src/WellGuide/constants';
import { isMobileBreakpoint } from '@zocdoc/components/src/utils';
import withAB from '../../../config/ab/withAB';
import { PINA_HOME_JANUARY_WELLNESS, VARIANT_OFF, VARIANT_ON } from '../../../config/ab/experiments';
import { INCOMPLETE_TASKS_THRESHOLD, sortRecommendations, MOBILE_INCOMPLETE_TASKS_THRESHOLD } from './constants';
import { JanuaryWellnessView } from './JanuaryWellnessView';
import { MobileJanuaryWellnessView } from './MobileJanuaryWellnessView';
import ThemedSearchPanel from '../ThemedSearchPanel';

const defaultIncompleteTasks = getSortedRecommendations(unauthorizedResponse(false));

const mapAbToProps = assignments => ({
    shouldShowJanuaryWellness: assignments[PINA_HOME_JANUARY_WELLNESS.experimentId] === VARIANT_ON,
});

export class JanuaryWellness extends Component {
    static propTypes = {
        fetchUser: PropTypes.func,
        getWellGuideData: PropTypes.func,
        subwayHomePageVariation: PropTypes.string,
    };

    static defaultProps = {
        fetchUser: noop,
        getWellGuideData: noop,
        subwayHomePageVariation: VARIANT_OFF,
    };

    state = {
        fetchingData: true,
        userLoginState: LOGIN_STATE.LoggedOut,
        recommendations: defaultIncompleteTasks,
        showModal: true,
    };

    async componentDidMount() {
        // NOTE: It's OK to call setState multiple times in lifecycle methods.
        // They are batched at the appropriate time.
        const {
            fetchUser,
            getWellGuideData,
        } = this.props;

        try {
            // Fetch User
            const user = await fetchUser();
            if (user) {
                this.setState({ userLoginState: user.login_state });
            } else {
                this.setState({ userLoginState: LOGIN_STATE.LoggedOut });
            }

            // Get Well Guide Status
            if (user && user.login_state === LOGIN_STATE.LoggedIn) {
                const { incompleteTasks } = await getWellGuideData();
                const recommendations = getSortedRecommendations(incompleteTasks);
                this.setState({ recommendations });
            } else {
                const recommendations = getSortedRecommendations(unauthorizedResponse(false));
                this.setState({ recommendations });
            }
        } catch (exc) {
            Raven.captureException(exc);
            this.setState({ recommendations: [] });
        }

        this.setState({ fetchingData: false });
    }

    renderJanuaryWellness = () => {
        const JanuaryWellnessAbView = props => {
            const {
                shouldShowJanuaryWellness,
            } = props;

            if (shouldShowJanuaryWellness) {
                return this.renderJanuaryWellnessView();
            }

            return (
                <ThemedSearchPanel subwayHomePageVariation={this.props.subwayHomePageVariation} />
            );
        };

        return withAB(PINA_HOME_JANUARY_WELLNESS, mapAbToProps)(JanuaryWellnessAbView);
    };

    renderJanuaryWellnessView = () => {
        const {
            recommendations,
            showModal,
        } = this.state;

        if (isMobileBreakpoint()) {
            const procedureId = recommendations.length ?
                recommendations[0].procedure_id : PROCEDURE_IDS.ANNUAL_PHYSICAL;
            const { userLoginState } = this.state;
            const loggedIn = userLoginState === LOGIN_STATE.LoggedIn;

            return (
                <MobileJanuaryWellnessView
                    data-test="january-wellness-mobile"
                    loggedIn={loggedIn}
                    onCancelModal={this.handleOnClickCancel}
                    procedureId={procedureId}
                    showModal={showModal}
                />
            );
        }

        return (
            <JanuaryWellnessView
                data-test="january-wellness"
                recommendations={recommendations}
            />
        );
    };

    render() {
        const {
            fetchingData,
            userLoginState,
            recommendations,
        } = this.state;

        if (fetchingData && !global.screenshot) {
            return null;
        }

        const incompleteTaskThreshold = isMobileBreakpoint() ?
            MOBILE_INCOMPLETE_TASKS_THRESHOLD : INCOMPLETE_TASKS_THRESHOLD;

        if (userLoginState === LOGIN_STATE.LoggedIn && recommendations.length < incompleteTaskThreshold) {
            return (
                <ThemedSearchPanel subwayHomePageVariation={this.props.subwayHomePageVariation} />
            );
        }

        const JanuaryWellnessAbComponent = this.renderJanuaryWellness();

        return (<JanuaryWellnessAbComponent data-test="january-wellness-ab-decision-container" />);
    }

    handleOnClickCancel = () => {
        this.setState({ showModal: false });
    };
}

function getSortedRecommendations(tasks) {
    const recommendations = tasks.filter(rec => rec.procedure_id !== PROCEDURE_IDS.SIGNED_UP_FOR_ZOCDOC);
    return recommendations.sort(sortRecommendations).slice(0, 3);
}
